/**
 * House-ad banner slot for the play-chrome top bar (sc-6959).
 *
 * Scope for now: stray-pixel ONLY — the AFK/ambient game whose long dwell
 * sessions (the natural-corner token drip, sc-6957) are the whole reason
 * an ad makes sense here. Every other cartridge leaves the slot dormant.
 *
 * The slot lives in shell chrome, never inside the game iframe — the game
 * has no idea ads exist. Creatives are IAB 728x90 leaderboards bundled
 * under /ads/ (no external requests — respects the cabinet-lock CSP).
 *
 * Reveal: immediate on mount (AD_REVEAL_MS = 0) — stray-pixel IS the ad
 * vehicle, so there's no reason to make the impression wait. The tick
 * runs off requestAnimationFrame (throttled in hidden tabs), so rotation
 * only advances on visible time; a delayed reveal is still supported if
 * AD_REVEAL_MS is ever raised.
 */

import { recordAdEvent } from '../api/client.js';
import { getOrCreateDeviceId } from '../runtime/identity.js';

export interface HouseAd {
  id: string;
  img: string;
  href: string;
  alt: string;
  weight: number;
}

/** Cross-promo across the Arcraz portfolio. Bundled 728x90 creatives. */
export const HOUSE_ADS: readonly HouseAd[] = [
  { id: 'parkpursuit', img: '/ads/parkpursuit-728x90.png', href: 'https://parkpursuit.app', alt: 'ParkPursuit — Walk. Ride. Win.', weight: 1 },
  { id: 'zyphr', img: '/ads/zyphr-728x90.png', href: 'https://zyphr.dev', alt: 'zyphr.dev — transactional messaging API', weight: 1 },
  { id: 'privateerprofits', img: '/ads/privateerprofits-728x90.png', href: 'https://bigego.games/games/privateerprofits', alt: 'Privateer Profits — rule the trade winds', weight: 1 },
  { id: 'bigegogames', img: '/ads/bigegogames-728x90.png', href: 'https://bigego.games', alt: 'Big Ego Games — games with an attitude', weight: 1 },
  { id: 'arcraz', img: '/ads/arcraz-728x90.png', href: 'https://arcraz.com', alt: 'Arcraz — Code. Craft. Deliver.', weight: 1 },
];

const AD_REVEAL_MS = 0;      // show immediately (stray-pixel is the ad vehicle)
const AD_ROTATE_MS = 15_000; // rotate creatives every 15s
const AD_ENABLED = true;      // kill switch (env-flag later, sc-6959)

/** Cartridges that get the slot. Only stray-pixel, for now. */
const AD_CARTRIDGES = new Set(['stray-pixel']);

export interface AdSlot {
  /** rAF-friendly tick; pass the frame delta in ms. */
  tick(deltaMs: number): void;
  /** Tear down: stop rotation, hide, drop listeners. */
  dispose(): void;
}

/** No-op slot for cartridges without ads (keeps play.ts branch-free). */
const NULL_SLOT: AdSlot = { tick: () => {}, dispose: () => {} };

/**
 * Weighted pick that avoids repeating the current ad when possible, so the
 * rotation feels varied instead of landing on the same creative twice.
 */
export function pickAd(exclude?: string): HouseAd {
  const pool = HOUSE_ADS.filter((a) => a.id !== exclude);
  const from = pool.length > 0 ? pool : HOUSE_ADS;
  const total = from.reduce((s, a) => s + a.weight, 0);
  let r = Math.random() * total;
  for (const ad of from) {
    r -= ad.weight;
    if (r <= 0) return ad;
  }
  return from[from.length - 1]!;
}

/**
 * Mount the ad slot into `chrome` for `cartridgeId`. Returns a live slot
 * the caller ticks each visible frame; a non-ad cartridge gets NULL_SLOT.
 */
export function mountAdSlot(chrome: HTMLElement, cartridgeId: string): AdSlot {
  if (!AD_ENABLED || !AD_CARTRIDGES.has(cartridgeId)) return NULL_SLOT;

  const slot = document.createElement('a');
  slot.className = 'play-chrome__ad';
  slot.target = '_blank';
  slot.rel = 'noopener noreferrer';
  slot.setAttribute('aria-label', 'Sponsor message');

  const img = document.createElement('img');
  img.width = 728;
  img.height = 90;
  img.decoding = 'async';
  slot.appendChild(img);
  // Insert before the mute control cluster so it centers between the
  // CABINET back button (left) and SOUND/hint (right).
  chrome.appendChild(slot);

  let visibleMs = 0;
  let revealed = false;
  let rotateAcc = 0;
  let current: HouseAd | null = null;

  function show(ad: HouseAd): void {
    current = ad;
    img.src = ad.img;
    img.alt = ad.alt;
    slot.href = ad.href;
    slot.dataset.adId = ad.id;
    // crossfade
    slot.classList.remove('is-fading');
    void slot.offsetWidth;
    slot.classList.add('is-visible');
    recordAdEvent({ slot: 'play-chrome', adId: ad.id, event: 'impression', cartridgeId, deviceId: getOrCreateDeviceId() });
  }

  const onClick = (): void => {
    if (current) recordAdEvent({ slot: 'play-chrome', adId: current.id, event: 'click', cartridgeId, deviceId: getOrCreateDeviceId() });
  };
  slot.addEventListener('click', onClick);

  // AD_REVEAL_MS === 0 → show on mount, no waiting for the first frame.
  if (AD_REVEAL_MS <= 0) {
    revealed = true;
    show(pickAd());
  }

  return {
    tick(deltaMs: number): void {
      visibleMs += deltaMs;
      if (!revealed) {
        if (visibleMs >= AD_REVEAL_MS) {
          revealed = true;
          show(pickAd());
        }
        return;
      }
      rotateAcc += deltaMs;
      if (rotateAcc >= AD_ROTATE_MS) {
        rotateAcc = 0;
        slot.classList.add('is-fading');
        // swap at the midpoint of the fade
        window.setTimeout(() => show(pickAd(current?.id)), 220);
      }
    },
    dispose(): void {
      slot.removeEventListener('click', onClick);
      slot.remove();
    },
  };
}
